/**
 * industryTag.ts
 * Qué hace: normaliza el rubro de un estilo (texto libre cargado por el admin) y
 *   le asigna SIEMPRE el mismo color.
 * Qué exporta: normalizeIndustry, industryKey, industryColors, INDUSTRY_PALETTE.
 * Quién lo usa: el picker de estilos y el editor de estilos.
 *
 * El rubro es texto libre por decisión de Charly, así que el color no puede
 * salir de una tabla fija: se deriva de un hash del texto normalizado contra una
 * paleta curada. Curada y no HSL al azar, porque el azar produce combinaciones
 * ilegibles (amarillo sobre blanco) y colores que chocan entre sí.
 */

export interface IndustryColors {
  bg: string;
  fg: string;
  border: string;
}

/** Pares fondo/texto verificados para contraste, en claro y en oscuro. */
export const INDUSTRY_PALETTE: IndustryColors[] = [
  { bg: 'rgba(59,130,246,0.16)', fg: '#1D4ED8', border: 'rgba(59,130,246,0.35)' },   // azul
  { bg: 'rgba(16,185,129,0.16)', fg: '#047857', border: 'rgba(16,185,129,0.35)' },   // verde
  { bg: 'rgba(245,158,11,0.18)', fg: '#B45309', border: 'rgba(245,158,11,0.38)' },   // ámbar
  { bg: 'rgba(139,92,246,0.16)', fg: '#6D28D9', border: 'rgba(139,92,246,0.35)' },   // violeta
  { bg: 'rgba(236,72,153,0.16)', fg: '#BE185D', border: 'rgba(236,72,153,0.35)' },   // rosa
  { bg: 'rgba(6,182,212,0.16)', fg: '#0E7490', border: 'rgba(6,182,212,0.35)' },     // cian
  { bg: 'rgba(239,68,68,0.16)', fg: '#B91C1C', border: 'rgba(239,68,68,0.35)' },     // rojo
  { bg: 'rgba(132,204,22,0.18)', fg: '#4D7C0F', border: 'rgba(132,204,22,0.38)' },   // lima
  { bg: 'rgba(249,115,22,0.16)', fg: '#C2410C', border: 'rgba(249,115,22,0.35)' },   // naranja
  { bg: 'rgba(20,184,166,0.16)', fg: '#0F766E', border: 'rgba(20,184,166,0.35)' },   // teal
];

/** Espaciado colapsado y recorte. Lo que se MUESTRA, tal como lo escribió el admin. */
export function normalizeIndustry(raw?: string | null): string {
  return (raw || '').trim().replace(/\s+/g, ' ').slice(0, 60);
}

// Marcas diacríticas combinantes (lo que deja NFD al separar los acentos).
// Escrito con escapes a propósito: como caracteres literales son invisibles en
// el editor y cualquier reencodeo del archivo los rompe en silencio.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Clave de agrupación: minúsculas y sin acentos, para que "Construcción" y
 * "construccion" caigan en el mismo filtro y con el mismo color. Sin esto, el
 * texto libre se fragmenta en variantes que parecen rubros distintos.
 */
export function industryKey(raw?: string | null): string {
  return normalizeIndustry(raw)
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '');
}

export function industryColors(raw?: string | null): IndustryColors {
  const key = industryKey(raw);
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return INDUSTRY_PALETTE[h % INDUSTRY_PALETTE.length];
}
